/**
 * Create Inventory Batch Page
 * Comprehensive batch creation form with validation and auto-calculation
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  CalendarIcon,
  Info,
  Package,
  Factory,
  Gauge,
  MapPin,
  CheckCircle2
} from 'lucide-react';

// Types and Validation
import {
  ProductType,
  QuantityUnit,
  QualityGrade,
  ProductTypeConfig,
  QuantityUnitConfig,
  QualityGradeConfig
} from '@/types/inventory';
import {
  createBatchSchema,
  formDataToApiRequest,
  getMinExpiryDate,
  getMaxProductionDate,
  type CreateBatchFormData
} from '@/schemas/inventory';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ============================================
// MAIN COMPONENT
// ============================================

export default function CreateBatchPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [puritySliderValue, setPuritySliderValue] = useState<number[]>([95]);
  const [productionDateOpen, setProductionDateOpen] = useState(false);
  const [expiryDateOpen, setExpiryDateOpen] = useState(false);

  // Initialize form with React Hook Form and Zod validation
  const form = useForm<CreateBatchFormData>({
    resolver: zodResolver(createBatchSchema),
    mode: 'onBlur',
    defaultValues: {
      productType: undefined,
      productionDate: undefined,
      expiryDate: undefined,
      initialQuantity: undefined,
      quantityUnit: undefined,
      qualityGrade: QualityGrade.A,
      purityPercentage: 95,
      pressure: undefined,
      storageLocation: '',
      pesoCompliant: false
    }
  });

  // Watch form values for conditional logic
  const productType = form.watch('productType');
  const productionDate = form.watch('productionDate');
  const purityPercentage = form.watch('purityPercentage');
  const qualityGrade = form.watch('qualityGrade');

  // Handle form submission
  const onSubmit = async (data: CreateBatchFormData) => {
    try {
      setIsSubmitting(true);

      // Convert form data to API request format
      const requestData = formDataToApiRequest(data);

      // Call API to create batch
      const response = await inventoryBatchService.createBatch(requestData);

      // Show success toast
      toast.success(`Batch Created Successfully!`, {
        description: `Batch ${response.batchNumber} has been created.`,
        duration: 5000,
        action: {
          label: 'View Batch',
          onClick: () => navigate(`/inventory/batches/${response.id}`)
        }
      });

      // Navigate to sales inventory page and refresh the list
      setTimeout(() => {
        navigate(`/sales/inventory`, {
          replace: true,
          state: { refreshData: true }
        });
      }, 1500);
    } catch (error: any) {
      // Show error toast
      toast.error('Failed to Create Batch', {
        description: error.message || 'An unexpected error occurred. Please try again.',
        duration: 6000
      });

      console.error('Batch creation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle purity slider change
  const handlePuritySliderChange = (value: number[]) => {
    setPuritySliderValue(value);
    form.setValue('purityPercentage', value[0], { shouldValidate: true });
  };


  // Check if pressure is required
  const isPressureRequired = productType === ProductType.COMPRESSED || productType === ProductType.BIO_CNG;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/sales/inventory')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Batches
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Create Inventory Batch</h1>
          <p className="text-gray-600 mt-2">
            Create a new biogas inventory batch with production details and quality metrics
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Batch Details Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <CardTitle>Batch Details</CardTitle>
                </div>
                <CardDescription>
                  Basic information about the inventory batch
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Product Type */}
                <FormField
                  control={form.control}
                  name="productType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Type *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select product type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(ProductTypeConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <span>{config.icon}</span>
                                <div>
                                  <div className="font-medium">{config.label}</div>
                                  <div className="text-xs text-gray-500">{config.description}</div>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Type of biogas product to be stored
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Production Date */}
                  <FormField
                    control={form.control}
                    name="productionDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Production Date *</FormLabel>
                        <Popover open={productionDateOpen} onOpenChange={setProductionDateOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick production date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                                setProductionDateOpen(false);
                              }}
                              disabled={(date) => date > getMaxProductionDate()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          Date when batch was produced
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Expiry Date */}
                  <FormField
                    control={form.control}
                    name="expiryDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Expiry Date *</FormLabel>
                        <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick expiry date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                                setExpiryDateOpen(false);
                              }}
                              disabled={(date) => date < getMinExpiryDate(productionDate)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          Must be after production date
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Initial Quantity */}
                  <FormField
                    control={form.control}
                    name="initialQuantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Initial Quantity *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.001"
                            placeholder="Enter quantity"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                          />
                        </FormControl>
                        <FormDescription>
                          Total quantity produced (max 3 decimal places)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Quantity Unit */}
                  <FormField
                    control={form.control}
                    name="quantityUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantity Unit *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.entries(QuantityUnitConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                {config.label} ({config.symbol})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Unit of measurement
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quality Metrics Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Factory className="h-5 w-5 text-green-600" />
                  <CardTitle>Quality Metrics</CardTitle>
                </div>
                <CardDescription>
                  Quality grade and purity specifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quality Grade */}
                <FormField
                  control={form.control}
                  name="qualityGrade"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Quality Grade *</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="grid grid-cols-1 md:grid-cols-3 gap-4"
                        >
                          {Object.entries(QualityGradeConfig).map(([key, config]) => (
                            <div key={key}>
                              <RadioGroupItem
                                value={key}
                                id={key}
                                className="peer sr-only"
                              />
                              <Label
                                htmlFor={key}
                                className={cn(
                                  'flex flex-col items-center justify-between rounded-lg border-2 border-gray-200 p-4 cursor-pointer',
                                  'hover:bg-gray-50 peer-data-[state=checked]:border-blue-600',
                                  'peer-data-[state=checked]:bg-blue-50',
                                  config.borderColor
                                )}
                              >
                                <span className={cn('text-2xl font-bold', config.color)}>
                                  Grade {key}
                                </span>
                                <span className="text-sm text-gray-600 text-center mt-2">
                                  {config.description}
                                </span>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Purity Percentage with Slider */}
                <FormField
                  control={form.control}
                  name="purityPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Purity Percentage *</FormLabel>
                        <span className="text-2xl font-bold text-blue-600">
                          {field.value?.toFixed(2) || 0}%
                        </span>
                      </div>
                      <FormControl>
                        <div className="space-y-4">
                          <Slider
                            min={0}
                            max={100}
                            step={0.1}
                            value={[field.value || 0]}
                            onValueChange={handlePuritySliderChange}
                            className="w-full"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="Enter purity %"
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value)) {
                                field.onChange(value);
                                setPuritySliderValue([value]);
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Methane content percentage (0-100%, max 2 decimal places)
                      </FormDescription>
                      <FormMessage />
                      {qualityGrade && purityPercentage !== undefined && (
                        <Alert className="mt-2">
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            {QualityGradeConfig[qualityGrade].description}
                          </AlertDescription>
                        </Alert>
                      )}
                    </FormItem>
                  )}
                />

                {/* Pressure (conditional) */}
                <FormField
                  control={form.control}
                  name="pressure"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Pressure (bar) {isPressureRequired && '*'}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Gauge className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={isPressureRequired ? 'Required for compressed products' : 'Optional'}
                            className="pl-10"
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        {isPressureRequired
                          ? 'Storage pressure is required for compressed products'
                          : 'Storage pressure (optional)'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Storage Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-600" />
                  <CardTitle>Storage Information</CardTitle>
                </div>
                <CardDescription>
                  Physical storage location and compliance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Storage Location */}
                <FormField
                  control={form.control}
                  name="storageLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Storage Location *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., Warehouse A, Section 2, Rack 5"
                          maxLength={255}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Physical location where batch is stored (max 255 characters)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* PESO Compliant (Optional) */}
                <FormField
                  control={form.control}
                  name="pesoCompliant"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          PESO Compliant
                        </FormLabel>
                        <FormDescription>
                          Check if this batch meets Petroleum and Explosives Safety Organisation standards
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Separator />

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-6 pb-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/inventory/batches')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[150px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Batch...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Create Batch
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
